import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon } from './shared';
import type { PowerSyncDatabase } from '@powersync/web';
import type { VitalDefinitionRow } from '@helios/data-access';
import { ensureEncounterForDate } from '../services/encounter-service';

// ─── Types ───

interface EncounterOption {
  id: string;
  label: string;
}

interface RecordVitalsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  patientId: string;
  vitalDefinitions: VitalDefinitionRow[];
  latestRecordedAt: string | null;
  db: PowerSyncDatabase | null;
  /** Optional queue/appointment context for linking vitals to a queue entry */
  queueEntryId?: string;
  appointmentId?: string;
  /** Pre-existing encounter ID — skips auto-create when provided */
  encounterId?: string;
  source?: string; // defaults to 'patient'
  /** Org + clinic context — needed to create visit/encounter when recording from queue */
  orgId?: string;
  clinicId?: string | null;
  providerId?: string | null;
  /** Mode: 'encounter' hides attach selector, 'patient' shows it */
  mode?: 'patient' | 'encounter';
  /** Today's encounters for the patient (used in patient mode for attach selector) */
  todayEncounters?: EncounterOption[];
}

// ─── Helpers ───

function formatRecordedAt(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Styles ───

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginBottom: 'var(--sp-4)',
      }}
    >
      <div style={{ color: 'var(--text-muted)' }}>
        <NavIcon name={icon} />
      </div>
      <span
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          lineHeight: 'var(--line-height-base)',
          letterSpacing: '-0.15px',
          color: 'var(--text)',
        }}
      >
        {title}
      </span>
    </div>
  );
}

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text)',
};

const inputBaseStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-base)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
  width: '100%',
};

const inputErrorStyle: CSSProperties = {
  ...inputBaseStyle,
  border: '1px solid var(--color-danger)',
};

const errorTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--sp-1)',
};

// ─── VitalInput ───

function VitalInput({
  def,
  value,
  error,
  onChange,
}: {
  def: VitalDefinitionRow;
  value: string;
  error: string | undefined;
  onChange: (val: string) => void;
}) {
  const isMandatory = def.mandatory === 1;

  if (def.value_type === 'boolean') {
    const checked = value === '1';
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={labelStyle}>
          {def.name}
          {isMandatory && <span style={{ color: 'var(--color-danger)' }}> *</span>}
        </label>
        <button
          role="switch"
          aria-checked={checked}
          onClick={() => onChange(checked ? '0' : '1')}
          style={{
            width: 44,
            height: 24,
            borderRadius: 'var(--radius-full)',
            background: checked ? 'var(--color-primary)' : 'var(--border)',
            border: 'none',
            padding: 2,
            cursor: 'pointer',
            transition: 'background 150ms ease',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              width: 20,
              height: 20,
              borderRadius: 'var(--radius-full)',
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              transition: 'transform 150ms ease',
              transform: checked ? 'translateX(20px)' : 'translateX(0)',
              display: 'block',
            }}
          />
        </button>
      </div>
    );
  }

  const inputType = def.value_type === 'date' ? 'date' : 'text';
  const inputMode =
    def.value_type === 'number' || def.value_type === 'integer' ? 'decimal' : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <label style={labelStyle}>
        {def.name}
        {isMandatory && <span style={{ color: 'var(--color-danger)' }}> *</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={inputType}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={def.unit ? `Enter ${def.name.toLowerCase()}` : undefined}
          style={{
            ...(error ? inputErrorStyle : inputBaseStyle),
            paddingRight: def.unit ? 48 : undefined,
          }}
        />
        {def.unit && def.value_type !== 'date' && (
          <span
            style={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-placeholder)',
              pointerEvents: 'none',
            }}
          >
            {def.unit}
          </span>
        )}
      </div>
      {error && <div style={errorTextStyle}>{error}</div>}
    </div>
  );
}

// ─── Main Component ───

export function RecordVitalsDrawer({
  open,
  onOpenChange,
  brandTokens,
  patientId,
  vitalDefinitions,
  latestRecordedAt,
  db,
  queueEntryId,
  appointmentId,
  encounterId: propEncounterId,
  source = 'patient',
  orgId,
  clinicId,
  providerId,
  mode = 'patient',
  todayEncounters = [],
}: RecordVitalsDrawerProps) {
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Encounter selector for patient mode
  const [selectedEncounterId, setSelectedEncounterId] = useState<string>('');

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      setFormValues({});
      setErrors({});
      setIsSaving(false);
      // Auto-select first encounter today if in patient mode
      if (mode === 'patient' && todayEncounters.length > 0) {
        setSelectedEncounterId(todayEncounters[0].id);
      } else {
        setSelectedEncounterId('');
      }
    }
  }, [open]); // intentionally omit mode/todayEncounters to avoid infinite re-render from array reference

  // Group definitions by category
  const { primaryDefs, additionalDefs } = useMemo(() => {
    const primary: VitalDefinitionRow[] = [];
    const additional: VitalDefinitionRow[] = [];
    for (const def of vitalDefinitions) {
      const cat = def.category ?? 'primary';
      if (cat === 'additional') additional.push(def);
      else primary.push(def);
    }
    return { primaryDefs: primary, additionalDefs: additional };
  }, [vitalDefinitions]);

  function handleChange(defId: string, val: string) {
    setFormValues((prev) => ({ ...prev, [defId]: val }));
    // Clear error when user types
    if (errors[defId]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[defId];
        return next;
      });
    }
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    for (const def of vitalDefinitions) {
      const val = formValues[def.id]?.trim() ?? '';
      if (def.mandatory === 1 && val === '') {
        newErrors[def.id] = `${def.name} is required`;
      }
      if (val !== '' && (def.value_type === 'number' || def.value_type === 'integer')) {
        const num = Number(val);
        if (isNaN(num)) {
          newErrors[def.id] = `${def.name} must be a valid number`;
        } else if (def.value_type === 'integer' && !Number.isInteger(num)) {
          newErrors[def.id] = `${def.name} must be a whole number`;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleSave = useCallback(async () => {
    if (!db || !patientId) return;
    if (!validate()) return;

    setIsSaving(true);
    try {
      // Use local ISO string so date comparisons match the local "today" date
      const now = new Date();
      const recordedAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`;

      // Determine encounter: explicit prop > patient-mode selection > the
      // queue day's encounter.
      let encounterId: string | null = propEncounterId ?? null;
      if (!encounterId && mode === 'patient' && selectedEncounterId) {
        encounterId = selectedEncounterId;
      }
      // From the queue, attach to the day's encounter — reuse the latest
      // encounter for today, creating one only if none exists. This shares the
      // reuse-or-create rule with the queue's "Open Visit" action so the two
      // can't spawn separate consultations for the same day. Patient-mode
      // "Not assigned" intentionally stays unattached (patient record only).
      if (!encounterId && source === 'queue' && orgId) {
        const visitDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const { encounterId: dayEncounterId } = await ensureEncounterForDate({
          db,
          orgId,
          clinicId: clinicId ?? null,
          patientId,
          date: visitDate,
          queueId: queueEntryId ?? null,
          appointmentId: appointmentId ?? null,
          providerId: providerId ?? null,
        });
        encounterId = dayEncounterId;
      }

      for (const def of vitalDefinitions) {
        const rawVal = formValues[def.id]?.trim() ?? '';
        if (rawVal === '') continue;

        const entryId = crypto.randomUUID();
        let valueNumber: number | null = null;
        let valueText: string | null = null;
        let valueDate: string | null = null;
        let valueBoolean: number | null = null;

        switch (def.value_type) {
          case 'number':
          case 'integer':
            valueNumber = Number(rawVal);
            break;
          case 'text':
            valueText = rawVal;
            break;
          case 'date':
            valueDate = rawVal;
            break;
          case 'boolean':
            valueBoolean = rawVal === '1' ? 1 : 0;
            break;
        }

        await db.execute(
          `INSERT INTO patient_vital_entries
            (id, patient_id, recorded_at, vital_definition_id,
             value_number, value_text, value_date, value_boolean, notes,
             queue_entry_id, appointment_id, encounter_id, source, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            entryId,
            patientId,
            recordedAt,
            def.id,
            valueNumber,
            valueText,
            valueDate,
            valueBoolean,
            null,
            queueEntryId || null,
            appointmentId || null,
            encounterId,
            source,
            recordedAt,
          ],
        );
      }

      onOpenChange(false);
    } catch (err) {
      console.error('[RecordVitals] Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [
    db,
    patientId,
    vitalDefinitions,
    formValues,
    onOpenChange,
    queueEntryId,
    appointmentId,
    propEncounterId,
    selectedEncounterId,
    mode,
    source,
    orgId,
    clinicId,
    providerId,
  ]);

  function renderSection(title: string, icon: string, defs: VitalDefinitionRow[]) {
    if (defs.length === 0) return null;
    return (
      <div>
        <SectionHeader icon={icon} title={title} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {defs.map((def) => (
            <VitalInput
              key={def.id}
              def={def}
              value={formValues[def.id] ?? ''}
              error={errors[def.id]}
              onChange={(val) => handleChange(def.id, val)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Record Vitals"
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Vitals'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
        }}
      >
        {/* Last Recorded Info Card */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-1)',
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-success-bg)',
            border: '1px solid var(--color-success)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <div style={{ color: 'var(--color-success)' }}>
              <NavIcon name="clock" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-sm)',
                color: 'var(--color-success-text)',
              }}
            >
              Last Recorded
            </span>
          </div>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
              paddingLeft: 26,
            }}
          >
            {latestRecordedAt ? formatRecordedAt(latestRecordedAt) : 'No previous recordings'}
          </span>
        </div>

        {/* Encounter selector (patient mode only) */}
        {mode === 'patient' && (
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text-muted)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Attach to Encounter (optional)
            </label>
            <select
              value={selectedEncounterId}
              onChange={(e) => setSelectedEncounterId(e.target.value)}
              style={{
                width: '100%',
                height: 36,
                padding: '0 10px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
              }}
            >
              <option value="">Not assigned</option>
              {todayEncounters.map((enc) => (
                <option key={enc.id} value={enc.id}>
                  {enc.label}
                </option>
              ))}
            </select>
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-placeholder)',
                marginTop: 4,
                display: 'block',
              }}
            >
              {selectedEncounterId
                ? 'Vitals will also appear in the Encounter vitals tab.'
                : 'Vitals will be saved to the patient record only.'}
            </span>
          </div>
        )}

        {/* Primary Vitals Section */}
        {renderSection('Primary Vitals', 'activity', primaryDefs)}

        {/* Additional Measurements Section */}
        {renderSection('Additional Measurements', 'bar-chart', additionalDefs)}
      </div>
    </Drawer>
  );
}
